import { useLazyQuery } from '@apollo/client';
import {
  EstateDetails,
  EstateListingDetailsResponseDto,
  EstateListingDetailsRequestDto,
  GET_ESTATE_DETAILS,
  GetEstateDetailsQuery,
  GetEstateListingDetailsT,
  EstateListingDetailsQueryActions,
  GetEstateDetailsByEstateIdQuery,
  GET_ESTATE_DETAILS_BY_ESTATE_ID,
  EstateListingDetailsByEstateIdResponseDto,
} from '@contexts/Estate';
import { ResponseApolloErrorMapping } from '@shared/data-mappers';
import { AptunoApolloLink } from '@shared/index';
import { BaseQueryResponse2, ResourceNotFoundError, Response } from '@shared/models';
import { option } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { useEffect, useState } from 'react';

export const GqlListingDetailsQueryActions: EstateListingDetailsQueryActions = {
  useGetEstateListingDetails: (): GetEstateListingDetailsT => {
    const [action, { data, loading, error, refetch }] = useLazyQuery<
      EstateListingDetailsResponseDto,
      EstateListingDetailsRequestDto
    >(GET_ESTATE_DETAILS, {
      errorPolicy: 'all',
    });

    const mapRecalling = (): Promise<EstateDetails | undefined> =>
      (refetch && refetch().then((response) => response.data.property)) || Promise.resolve(undefined);

    return {
      loading,
      recall: mapRecalling,
      error: error && ResponseApolloErrorMapping(error),
      estateListingDetails: data?.property,
      getEstateListingDetails: (query: GetEstateDetailsQuery) =>
        action({
          variables: {
            propertyId: query.estateId,
          },
        }),
    };
  },

  useGetEstateListingDetailsByEstateId: function (): BaseQueryResponse2<
    GetEstateDetailsByEstateIdQuery,
    EstateDetails
  > {
    const [action, { data, loading, error, refetch, called }] = useLazyQuery<EstateListingDetailsByEstateIdResponseDto>(
      GET_ESTATE_DETAILS_BY_ESTATE_ID,
      {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CORE },
      }
    );

    const [result, setResult] = useState<Response<EstateDetails>>(Response.notPerformed);

    useEffect(() => {
      setResult((current) =>
        pipe(
          option.fromNullable(error),

          option.map((error) => {
            const mappedError = ResponseApolloErrorMapping(error);
            return new Response.Failure(mappedError);
          }),

          option.getOrElse(() => current)
        )
      );
    }, [error]);

    useEffect(() => {
      const wasExecuted = !!data && called;

      if (wasExecuted) {
        const foundEstate = data?.getEstate;

        if (foundEstate) setResult(() => new Response.Success(foundEstate));
        else
          setResult(() => {
            const error = new ResourceNotFoundError('getEstateListingDetailsByEstateId');
            return new Response.Failure(error);
          });
      }
    }, [data?.getEstate, called]);

    const recall = async (): Promise<void> => refetch().then(() => void 0);

    return {
      loading,
      recall,
      result,
      get: (query: GetEstateDetailsByEstateIdQuery) =>
        action({
          variables: {
            estateId: query.estateId.value,
          },
        }),
    };
  },
};
